import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import { AlertTriangle, FileDown, Printer, X } from "@/lib/icons";
import { formatDateOnly, formatTimestampInTimezone, errorWords } from "@alm/domain";
import { useState } from "react";
import { toast } from "sonner";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { saveFile, showFile } from "@/lib/files/download";
import { orpc, orpcClient, type Output } from "@/lib/orpc";
import { optionalDay, optionalString, KEEP_PLACE } from "@/lib/search";
import { useCan } from "@/lib/policy";
import { useZone } from "@/lib/facility/zone";
import { IncidentActionsPanel } from "@/routes/_authenticated/_care/incidents/-components/incident-actions-panel";
import { IncidentDialog } from "@/routes/_authenticated/_care/incidents/-components/incident-dialog";
import { INCIDENT_DATE_PRESETS, inDateRange } from "./-components/chart/incident-date-presets";
import { SectionEmpty } from "@/routes/_authenticated/_care/residents/$residentId/-components/chart/section-empty";
import { useAllIncidents } from "@/routes/_authenticated/_care/incidents/-components/all-incidents";
import { prettify } from "@/routes/_authenticated/_care/incidents/-components/incident-table-shared";

const SEVERITY_TONE: Record<string, "neutral" | "info" | "warn" | "crit"> = {
  low: "neutral",
  medium: "info",
  high: "warn",
  critical: "crit",
};

type IncidentsSearch = { preset?: string; from?: string; to?: string };

export const Route = createFileRoute("/_authenticated/_care/residents/$residentId/incidents")({
  validateSearch: (search: Record<string, unknown>): IncidentsSearch => ({
    preset: optionalString(search.preset)?.slice(0, 30) || undefined,
    from: optionalDay(search.from),
    to: optionalDay(search.to),
  }),
  component: ResidentIncidentsTab,
});

type Incident = Output<typeof orpcClient.care.incidents.list>["rows"][number];

const fail = (error: Error) => toast.error(errorWords(error));

/** This resident's incident reports: date range, batch print/export, and each report in full. */
function ResidentIncidentsTab() {
  const { residentId } = Route.useParams();
  const { data: resident } = useSuspenseQuery(
    orpc.care.residents.get.queryOptions({ input: { residentId } }),
  );
  const canManage = useCan("incident:manage", resident.facilityId);
  const zone = useZone(resident.facilityId);
  const params = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const patch = (next: Partial<IncidentsSearch>) =>
    void navigate({ search: (prev) => ({ ...prev, ...next }), ...KEEP_PLACE });
  const preset = params.preset ?? "all";
  const from = params.from ?? "";
  const to = params.to ?? "";
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState<null | "print" | "export">(null);
  const [dialog, setDialog] = useState<{ incident?: Incident } | null>(null);

  const incidents = useAllIncidents({ residentId });
  const print = useMutation(
    orpc.care.incidents.exportPdf.mutationOptions({ onSuccess: showFile, onError: fail }),
  );

  const all = incidents.data?.rows ?? [];
  const visible = all.filter((row) => inDateRange(row.reportedAt, from, to));
  const selectedIds = visible.filter((row) => selected.has(row.id)).map((row) => row.id);
  const allSelected = visible.length > 0 && selectedIds.length === visible.length;
  const openCount = visible.filter((row) => row.status !== "resolved").length;
  const dateFiltered = Boolean(from || to);

  const applyPreset = (value: string) => {
    const range = INCIDENT_DATE_PRESETS.find((p) => p.value === value)?.range?.();
    patch({
      preset: value === "all" ? undefined : value,
      ...(range ? { from: range[0], to: range[1] } : {}),
    });
  };
  const runBatch = async (mode: "print" | "export") => {
    if (selectedIds.length === 0) return;
    setBusy(mode);
    try {
      const file = await orpcClient.care.incidents.exportBatchPdf({ incidentIds: selectedIds });
      if (mode === "print") showFile(file);
      else saveFile(file);
    } catch (error) {
      fail(error as Error);
    } finally {
      setBusy(null);
    }
  };
  const report = () => setDialog({});

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold">Incident reports</h2>
          <p className="text-muted-foreground mt-0.5 text-sm">
            {visible.length > 0
              ? `${openCount} of ${visible.length} still open${dateFiltered ? ` (${all.length} total)` : ""}`
              : "Safety incidents for this resident"}
          </p>
        </div>
        {canManage ? (
          <Button onClick={report}>
            <AlertTriangle className="size-4" />
            Report incident
          </Button>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {/* Only with something to select, and saying what it does: a bare
            box in front of the date filters read as one of them. */}
        {visible.length > 0 ? (
          <span className="flex items-center gap-2 pe-2">
            <Checkbox
              id="incidents-select-all"
              checked={allSelected}
              onCheckedChange={(checked) =>
                setSelected(checked ? new Set(visible.map((row) => row.id)) : new Set())
              }
            />
            <label htmlFor="incidents-select-all" className="text-muted-foreground text-sm">
              Select all<span className="sr-only"> incidents</span>
            </label>
          </span>
        ) : null}
        <Select value={preset} onValueChange={applyPreset}>
          <SelectTrigger className="w-40" aria-label="Date range preset">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {INCIDENT_DATE_PRESETS.map((p) => (
              <SelectItem key={p.value} value={p.value}>
                {p.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          type="date"
          className="w-40"
          aria-label="From date"
          value={from}
          onChange={(e) => {
            patch({ from: e.target.value || undefined, preset: "custom" });
          }}
        />
        <Input
          type="date"
          className="w-40"
          aria-label="To date"
          value={to}
          onChange={(e) => {
            patch({ to: e.target.value || undefined, preset: "custom" });
          }}
        />
        {dateFiltered ? (
          <Button variant="ghost" onClick={() => applyPreset("all")}>
            Clear dates
          </Button>
        ) : null}
        {selectedIds.length > 0 ? (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-muted-foreground text-sm">{selectedIds.length} selected</span>
            <Button
              variant="outline"
              disabled={busy !== null}
              onClick={() => void runBatch("print")}
            >
              <Printer className="size-4" />
              Print
            </Button>
            <Button
              variant="outline"
              disabled={busy !== null}
              onClick={() => void runBatch("export")}
            >
              <FileDown className="size-4" />
              Export PDF
            </Button>
            <Button
              variant="ghost"
              size="icon"
              aria-label="Clear selection"
              onClick={() => setSelected(new Set())}
            >
              <X className="size-4" />
            </Button>
          </div>
        ) : null}
      </div>

      {incidents.isPending ? (
        <div className="flex flex-col gap-3" aria-busy="true">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      ) : incidents.isError ? (
        <ReadFailed
          message="Could not load this resident's incidents."
          error={incidents.error}
          onRetry={() => void incidents.refetch()}
          retrying={incidents.isFetching}
        />
      ) : visible.length === 0 ? (
        <Card>
          <SectionEmpty
            title={dateFiltered ? "No incidents in this date range" : "No incidents reported"}
            detail={
              dateFiltered
                ? "Widen the date range or clear it to see all incidents."
                : "Falls, medication errors and other safety events appear here once reported."
            }
          />
        </Card>
      ) : (
        <div className="flex flex-col gap-3">
          {visible.map((incident) => (
            <Card key={incident.id} className="gap-0 py-0">
              <div className="flex flex-wrap items-start justify-between gap-3 border-b px-6 py-4">
                <div className="flex min-w-0 flex-1 items-start gap-3">
                  <Checkbox
                    className="mt-0.5"
                    aria-label={`Select the incident reported ${formatDateOnly(incident.reportedAt, zone)}`}
                    checked={selected.has(incident.id)}
                    onCheckedChange={() =>
                      setSelected((current) => {
                        const next = new Set(current);
                        if (next.has(incident.id)) next.delete(incident.id);
                        else next.add(incident.id);
                        return next;
                      })
                    }
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant="secondary">{prettify(incident.incidentType)}</Badge>
                      <Badge variant={SEVERITY_TONE[incident.severity] ?? "neutral"}>
                        {prettify(incident.severity)}
                      </Badge>
                      <Badge variant={incident.status === "resolved" ? "ok" : "outline"}>
                        {prettify(incident.status)}
                      </Badge>
                    </div>
                    <p className="text-muted-foreground mt-1.5 text-xs tabular-nums">
                      Reported {formatTimestampInTimezone(incident.reportedAt, zone)}
                    </p>
                  </div>
                </div>
                <div className="flex shrink-0 gap-2">
                  <Button
                    variant="outline"
                    disabled={print.isPending}
                    onClick={() => print.mutate({ incidentId: incident.id })}
                  >
                    Print
                  </Button>
                  {canManage ? (
                    <Button variant="outline" onClick={() => setDialog({ incident })}>
                      Edit
                    </Button>
                  ) : null}
                </div>
              </div>
              <div className="flex flex-col gap-4 px-6 py-4">
                <div>
                  <p className="text-muted-foreground text-xs font-medium">Description</p>
                  <p className="mt-1 text-sm whitespace-pre-wrap">{incident.description}</p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs font-medium">Actions taken</p>
                  <IncidentActionsPanel incidentId={incident.id} />
                </div>
                {incident.followUpRequired ? (
                  <Badge variant="warn" className="self-start">
                    Follow-up required
                  </Badge>
                ) : null}
                {incident.resolvedAt ? (
                  <div>
                    <p className="text-muted-foreground text-xs font-medium">Resolved</p>
                    <p className="mt-1 text-sm tabular-nums">
                      {formatTimestampInTimezone(incident.resolvedAt, zone)}
                    </p>
                  </div>
                ) : null}
              </div>
            </Card>
          ))}
        </div>
      )}

      {dialog ? (
        <IncidentDialog
          incident={dialog.incident ?? null}
          residentId={residentId}
          residents={[]}
          onClose={() => setDialog(null)}
        />
      ) : null}
    </div>
  );
}
